import {
  ElementType,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  Input,
  ScaleFade,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  FiClock,
  FiDatabase,
  FiFileText,
  FiGitBranch,
  FiGrid,
  FiInbox,
  FiPieChart,
  FiSearch,
  FiSmile,
  FiStar,
  FiZap,
} from 'react-icons/fi';
import { useLocation } from 'react-router-dom';

import { HeaderUp } from '../../../components/_components_0.2/Header_0.2';
import { Sidebar } from '../../../components/_components_0.2/Sidebar_0.2';
import { apllyToast } from '../../../components/Global/Toast2.0';
import { SetTitleContext } from '../../../contexts/SetTitleBIContext';
import {
  filterBiReports,
  groupBiReports,
  useBiGroups,
  useToggleBiFavorite,
  useVisibleBiReports,
} from '../../../hooks/useBi';
import { useCan } from '../../../hooks/useCan';
import { BiReport } from '../../../services/biService';
import {
  BiAppearance,
  resolveAppearance,
  sortGroups,
} from '../../../utils/biAppearance';
import { matchesSearch } from '../../../utils/biCatalog';
import { DataLiteracyCycle } from './DataLiteracyCycle';
import { DataManagementCard, DataManagementItem } from './DataManagementCard';
import { ReportCard } from '../../../components/_components_0.2/BI/ReportCard';

const RECENT_LIMIT = 4;
const DATA_GROUP = 'Gestão de dados';
const GRID_COLUMNS = { base: 1, md: 2, lg: 3, xl: 4 };
const DATA_APPEARANCE: BiAppearance = {
  color: 'green',
  icon: FiDatabase,
  iconKey: 'database',
};

function SectionTitle({
  icon,
  color,
  count,
  children,
}: {
  icon: ElementType;
  color: string;
  count?: number;
  children: ReactNode;
}) {
  return (
    <Flex align="center" gridGap="2" mb="3">
      <Flex
        w="7"
        h="7"
        flexShrink={0}
        borderRadius="md"
        bg={`${color}.100`}
        color={`${color}.600`}
        align="center"
        justify="center"
      >
        <Icon as={icon} />
      </Flex>
      <Text fontWeight="bold" color="gray.50">
        {children}
      </Text>
      {count !== undefined && (
        <Text fontSize="sm" color="gray.500">
          {count === 1 ? '1 item' : `${count} itens`}
        </Text>
      )}
    </Flex>
  );
}

function GroupChip({
  label,
  count,
  appearance,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  appearance: Pick<BiAppearance, 'color' | 'icon'>;
  isActive: boolean;
  onClick: () => void;
}) {
  const iconColor = isActive ? 'white' : `${appearance.color}.400`;

  return (
    <Button
      size="sm"
      borderRadius="full"
      variant={isActive ? 'solid' : 'outline'}
      colorScheme={isActive ? appearance.color : 'gray'}
      color={isActive ? 'white' : 'gray.50'}
      leftIcon={<Icon as={appearance.icon} color={iconColor} />}
      aria-pressed={isActive}
      onClick={onClick}
    >
      {label}
      <Text as="span" ml="1.5" opacity={0.7} fontWeight="normal">
        {count}
      </Text>
    </Button>
  );
}

export default function BIManagementDashboard() {
  const { search: locationSearch } = useLocation();
  const { handleSetTitle } = useContext(SetTitleContext);
  const { reports, isLoading, isError, refetch } = useVisibleBiReports();
  const { byName } = useBiGroups();
  const toggleFavorite = useToggleBiFavorite();

  const [search, setSearch] = useState(
    () => new URLSearchParams(locationSearch).get('q') || '',
  );
  const [group, setGroup] = useState('');

  useEffect(() => {
    handleSetTitle('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Telas de "Gestão de dados" do menu lateral, com as mesmas permissões.
  const canSeeProcesses = useCan({ permissions: ['ti_data'] });
  const canSeeEnergy = useCan({ permissions: ['edit_energy'] });
  const canSeeClimate = useCan({ permissions: ['edit_climate_research'] });

  const dataItems = useMemo(() => {
    const items: (DataManagementItem & { allowed: boolean })[] = [
      {
        key: 'income',
        title: 'Demonstrativo de resultados',
        description: 'Resultado financeiro',
        to: '/management-ppr/report-income-statement',
        icon: FiFileText,
        allowed: true,
      },
      {
        key: 'processes',
        title: 'Mapeamento de processos',
        description: 'Gerenciar mapeamento',
        to: '/process-management',
        icon: FiGitBranch,
        allowed: canSeeProcesses,
      },
      {
        key: 'energy',
        title: 'Custos de energia',
        description: 'Gerenciar custos de energia',
        to: '/management-energy/energy-table',
        icon: FiZap,
        allowed: canSeeEnergy,
      },
      {
        key: 'climate',
        title: 'Pesquisa de clima',
        description: 'Gerenciar pesquisas',
        to: '/climate-research-management',
        icon: FiSmile,
        allowed: canSeeClimate,
      },
    ];

    return items.filter(item => item.allowed);
  }, [canSeeProcesses, canSeeEnergy, canSeeClimate]);

  const appearanceOf = (name: string) =>
    resolveAppearance(null, byName[name], name);
  const reportAppearance = (report: BiReport) =>
    resolveAppearance(
      report,
      byName[report.groupNameSamasc || 'Sem grupo'],
      report.groupNameSamasc,
    );

  // Grupos na ordem definida no admin, com a quantidade de relatórios.
  const groups = useMemo(
    () =>
      sortGroups(groupBiReports(reports), byName).map(item => ({
        name: item.group,
        count: item.reports.length,
      })),
    [reports, byName],
  );

  const visibleDataItems =
    !group || group === DATA_GROUP
      ? dataItems.filter(item =>
          matchesSearch(
            `${item.title} ${item.description} ${DATA_GROUP}`,
            search,
          ),
        )
      : [];

  const sections = useMemo(() => {
    if (group === DATA_GROUP) {
      return [];
    }

    const byGroup = group
      ? reports.filter(
          report => (report.groupNameSamasc || 'Sem grupo') === group,
        )
      : reports;
    return sortGroups(groupBiReports(filterBiReports(byGroup, search)), byName);
  }, [reports, group, search, byName]);

  const isFiltering = !!search.trim() || !!group;
  const favorites = reports.filter(report => report.isFavorite);
  const recent = reports
    .filter(report => report.lastViewedAt)
    .sort(
      (a, b) =>
        new Date(b.lastViewedAt as string).getTime() -
        new Date(a.lastViewedAt as string).getTime(),
    )
    .slice(0, RECENT_LIMIT);

  const handleToggleFavorite = (report: BiReport) => {
    toggleFavorite.mutate(report, {
      onError: () =>
        apllyToast('error', 'Não foi possível atualizar os favoritos.'),
    });
  };

  const handleSelectGroup = (name: string) => {
    setGroup(current => (current === name ? '' : name));
  };

  const renderGrid = (items: BiReport[], showGroup = true) => {
    return (
      <SimpleGrid columns={GRID_COLUMNS} spacing="4">
        {items.map(report => (
          <ReportCard
            key={report.id}
            report={report}
            appearance={reportAppearance(report)}
            showGroup={showGroup}
            onToggleFavorite={handleToggleFavorite}
          />
        ))}
      </SimpleGrid>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex minH="260px" align="center" justify="center">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      );
    }

    if (isError) {
      return (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription flex="1">
            Não foi possível carregar os relatórios.
          </AlertDescription>
          <Button size="sm" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </Alert>
      );
    }

    if (sections.length === 0 && visibleDataItems.length === 0) {
      return (
        <Center py="10" flexDirection="column" textAlign="center">
          <Icon as={FiInbox} fontSize="40" color="gray.500" />
          <Text color="gray.400" mt="3">
            Nenhum relatório encontrado
          </Text>
          <Text color="gray.500" fontSize="sm">
            {isFiltering
              ? 'Confira a busca ou o grupo selecionado.'
              : 'Fale com o administrador para liberar o acesso aos relatórios.'}
          </Text>
          {isFiltering && (
            <Button
              size="sm"
              mt="4"
              variant="outline"
              onClick={() => {
                setSearch('');
                setGroup('');
              }}
            >
              Limpar filtros
            </Button>
          )}
        </Center>
      );
    }

    return (
      <Stack spacing="8">
        {!isFiltering && favorites.length > 0 && (
          <Box>
            <SectionTitle icon={FiStar} color="yellow">
              Favoritos
            </SectionTitle>
            {renderGrid(favorites)}
          </Box>
        )}

        {!isFiltering && recent.length > 0 && (
          <Box>
            <SectionTitle icon={FiClock} color="gray">
              Vistos recentemente
            </SectionTitle>
            {renderGrid(recent)}
          </Box>
        )}

        {sections.map(item => {
          const appearance = appearanceOf(item.group);

          return (
            <Box key={item.group}>
              <SectionTitle
                icon={appearance.icon}
                color={appearance.color}
                count={item.reports.length}
              >
                {item.group}
              </SectionTitle>
              {renderGrid(item.reports, false)}
            </Box>
          );
        })}

        {visibleDataItems.length > 0 && (
          <Box>
            <SectionTitle
              icon={DATA_APPEARANCE.icon}
              color={DATA_APPEARANCE.color}
              count={visibleDataItems.length}
            >
              {DATA_GROUP}
            </SectionTitle>
            <SimpleGrid columns={GRID_COLUMNS} spacing="4">
              {visibleDataItems.map(item => (
                <DataManagementCard key={item.key} item={item} />
              ))}
            </SimpleGrid>
          </Box>
        )}

        {!isFiltering && <DataLiteracyCycle />}
      </Stack>
    );
  };

  return (
    <ScaleFade initialScale={0.9} in>
      <HeaderUp path="bi" />
      <Sidebar path="bi" />

      <Box maxW="1480px" mx="auto" px={{ base: 4, md: 6 }} pb="10">
        <Flex
          mt="6"
          mb="4"
          align={{ base: 'flex-start', md: 'center' }}
          justify="space-between"
          direction={{ base: 'column', md: 'row' }}
          gridGap="4"
        >
          <Box>
            <Flex align="center" gridGap="3">
              <Icon as={FiPieChart} color="blue.500" boxSize="7" />
              <Heading size="lg">Inteligência de negócios</Heading>
            </Flex>
            <Text mt="1" color="gray.500">
              Relatórios da COOASGO liberados para você. Clique em um card para
              abrir.
            </Text>
          </Box>

          <Box position="relative" w={{ base: '100%', md: '360px' }}>
            <Icon
              as={FiSearch}
              position="absolute"
              left="3"
              top="50%"
              transform="translateY(-50%)"
              color="gray.400"
              zIndex={1}
            />
            <Input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Buscar por nome, setor ou assunto"
              aria-label="Buscar relatório"
              pl="10"
            />
          </Box>
        </Flex>

        {!isLoading && !isError && (groups.length > 0 || dataItems.length > 0) && (
          <Flex
            wrap="wrap"
            gridGap="2"
            mb="6"
            role="group"
            aria-label="Filtrar por grupo"
          >
            <GroupChip
              label="Todos"
              count={reports.length}
              appearance={{ color: 'blue', icon: FiGrid }}
              isActive={!group}
              onClick={() => setGroup('')}
            />
            {groups.map(item => (
              <GroupChip
                key={item.name}
                label={item.name}
                count={item.count}
                appearance={appearanceOf(item.name)}
                isActive={group === item.name}
                onClick={() => handleSelectGroup(item.name)}
              />
            ))}
            {dataItems.length > 0 && (
              <GroupChip
                label={DATA_GROUP}
                count={dataItems.length}
                appearance={DATA_APPEARANCE}
                isActive={group === DATA_GROUP}
                onClick={() => handleSelectGroup(DATA_GROUP)}
              />
            )}
          </Flex>
        )}

        {!isLoading &&
          !isError &&
          !isFiltering &&
          reports.length > 0 &&
          favorites.length === 0 && (
            <Flex
              align="center"
              gridGap="2"
              mb="6"
              fontSize="sm"
              color="gray.500"
            >
              <Icon as={FiStar} color="yellow.400" />
              <Text>
                Dica: clique na estrela de um relatório para deixá-lo em
                Favoritos, no topo desta página e no menu lateral.
              </Text>
            </Flex>
          )}

        {renderContent()}
      </Box>
    </ScaleFade>
  );
}
